import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import throttle from 'lodash.throttle';
import Reveal from 'react-reveal/Reveal';
import LocalizationContext from '../../contexts/Localizations';
import FiltersPanel from '../FiltersPanel';
import Container from '../helpers/Container';
import { getUrlParamValue, scrollIntoView } from '../../utilities/browser';
import { Loader, Pagination, SvgImage } from '../common';
import RidersListProps from './type';
import { Rider, Team } from '../../core/typings/riders';
import { RidersApiParams } from '../../core/typings/api';
import api from '../../core/api/models';

import './styles.scss';

export interface RidersListApiParams {
  id?: string;
  page?: number;
  countryCode?: string;
  discipline?: string;
  category?: string;
  year?: string;
  search?: string;
}

export const TeamItem = ({
  countryCode,
  teamCode,
  teamName,
  format,
  url,
}: Team) => {
  const WithLink = ({ children, ...props }: { children: JSX.Element[], className: string }) => (url
    ? <a href={url} {...props}>{children}</a>
    : <div {...props}>{children}</div>);

  return (
    <li className="riders-list__item">
      <WithLink className="riders-list__item-container">
        <div className="riders-list__details">
          <div className="riders-list__teamname">
            {teamName}
          </div>
          {teamCode && (
            <div className="riders-list__team" aria-label={teamName}>
              {teamCode}
            </div>
          )}
        </div>
        <div className="riders-list__meta">
          <div className="riders-list__format" aria-label={format}>
            {format}
          </div>
          {countryCode && (
            <div className="riders-list__country" aria-label={countryCode}>
              {countryCode.replace('RUS', '***').replace('BLR', '***')}
            </div>
          )}
        </div>
        <div className="riders-list__placeholder">
          {url && (
            <SvgImage ariaHidden title="" reference="#icon-think-arrow" />
          )}
        </div>
      </WithLink>
    </li>
  );
};

export const RiderItem = ({
  statusCode,
  givenName,
  familyName,
  teamCode,
  teamName,
  countryCode,
  countryName,
  format,
  url,
}: Rider) => {
  const WithLink = ({ children, ...props }: { children: JSX.Element[], className: string }) => (url
    ? <a href={url} {...props}>{children}</a>
    : <div {...props}>{children}</div>);

  return (
    <li className={`riders-list__item ${statusCode ? `riders-list__item--${statusCode}` : ''}`}>
      <WithLink className="riders-list__item-container">
        <div className="riders-list__details">
          <div className="riders-list__firstname">{givenName}</div>
          <div className="riders-list__lastname">{familyName}</div>
          {teamCode && (
            <div className="riders-list__team" aria-label={teamName}>
              {teamCode}
            </div>
          )}
        </div>

        <div className="riders-list__meta">
          <div className="riders-list__format" aria-label={format}>
            {format}
          </div>
          {countryCode && (
            <div className="riders-list__country" aria-label={countryName}>
              {countryCode.replace('RUS', '***').replace('BLR', '***')}
            </div>
          )}
        </div>
      </WithLink>
    </li>
  );
};

const RidersList = ({
  title,
  competitionHubEntryId,
  disciplineCode,
  filters,
  statuses,
  forCompetitionHub,
  forTeams,
}: RidersListProps) => {
  const resultsContainer = useRef<HTMLDivElement>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [riders, setRiders] = useState<Rider[] | Team[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(0);
  const [apiQueryParams, setApiQueryParams] = useState<RidersApiParams>();
  const { translations } = useContext(LocalizationContext);

  let apiEndpoint = api.riders.getRiders;

  if (forCompetitionHub) {
    apiEndpoint = api.riders.getCompetitionRiders;
  } else {
    apiEndpoint = forTeams ? api.riders.getTeams : api.riders.getRiders;
  }

  /**
   * Handles the default api params.
   */
  const getDefaultApiParams = () => {
    const state = {
      ...apiQueryParams,
      page: getUrlParamValue('page') || 1,
    };
    if (disciplineCode) {
      state.discipline = disciplineCode;
    }

    if (filters) {
      const hasYearFilter = filters.find((filter) => filter.queryParam === 'year');
      if (hasYearFilter) {
        state.year = hasYearFilter.items[0].code;
      }
    }

    if (competitionHubEntryId) {
      state.id = competitionHubEntryId;
    }
    return state;
  };

  /**
   * Calls a riders endpoint
   */
  const fetchRiders = async (params: RidersApiParams) => {
    setIsFetching(true);
    try {
      const response = await apiEndpoint(params);

      setRiders(response.items);
      setTotalItems(response.totalItems);
      setPageSize(response.pageSize);
      setIsFetching(false);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Handles the filter change in the <FiltersPanel>
   * @param params
   */
  const handleFilterChange = (params: any) => {
    const state = {
      ...apiQueryParams,
      ...params.selected,
      countryCode: params.selected.country,
      page: 1,
    };
    setApiQueryParams(state);

    if (resultsContainer.current) {
      scrollIntoView(resultsContainer.current, 'smooth');
    }
  };

  const throttledFetchRiders = useCallback(
    throttle(fetchRiders, 250, { leading: false }), [],
  );

  const handleSearch = (searchTerm: any) => {
    const state = {
      ...getDefaultApiParams(),
      search: searchTerm,
      page: 1,
    };
    setApiQueryParams(state);

    if (resultsContainer.current) {
      scrollIntoView(resultsContainer.current, 'smooth');
    }
  };

  const handlePageChange = (page: number) => {
    const state = { ...apiQueryParams, page };
    setApiQueryParams(state);

    if (resultsContainer.current) {
      scrollIntoView(resultsContainer.current, 'smooth');
    }
  };

  // testing
  useEffect(() => {
    if (apiQueryParams) {
      if (apiQueryParams.search) {
        throttledFetchRiders(apiQueryParams);
      } else {
        fetchRiders(apiQueryParams);
      }
    }
  }, [apiQueryParams]);

  useEffect(() => {
    setApiQueryParams(
      getDefaultApiParams() as RidersApiParams,
    );
  }, []);

  return (
    <div className="riders-list">
      <Container>
        <div className="riders-list__content">
          {filters && (
            <div className="riders-list__filters">
              <FiltersPanel
                hasSearch
                visibleFilters={filters}
                visibleFilterCallback={handleFilterChange}
                searchLabel={translations.riders.searchText}
                searchPlaceholder={translations.calendar.searchLabel}
                searchFieldCallback={handleSearch}
                searchTerm={apiQueryParams?.search}
              />
            </div>
          )}

          <div className="riders-list__body">
            {title && (
              <h3 className="riders-list__title">{title}</h3>
            )}

            {isFetching && (
              <div className="riders-list__loader">
                <Loader />
              </div>
            )}

            {Boolean(!isFetching && !riders.length) && (
              <div className="riders-list__no-results">
                <h3>
                  {translations.calendar.noResults}
                </h3>
              </div>
            )}

            {Boolean(!isFetching && riders.length) && (
              <div className="riders-list__results" ref={resultsContainer}>
                {!forCompetitionHub && (
                  <div className="riders-list__count">
                    {`${totalItems} ${translations.riders.resultsLabel}`}
                  </div>
                )}

                <ul className="riders-list__list">
                  {riders.map((item, index) => (
                    <>
                      {!forTeams && (
                        <Reveal effect="shortFadeInUp" delay={index * 20}>
                          <RiderItem {...item as Rider} key={String(Math.random())} />
                        </Reveal>

                      )}
                      {forTeams && (
                        <Reveal effect="shortFadeInUp" delay={index * 20}>
                          <TeamItem {...item as Team} key={String(Math.random())} />
                        </Reveal>
                      )}
                    </>
                  ))}
                </ul>

                {statuses && (
                  <div className="riders-list__sanction-types">
                    {statuses.map((status) => (
                      <div className={`sanction sanction--${status.statusCode}`}>
                        {status.label}
                      </div>
                    ))}
                  </div>
                )}

                {apiQueryParams?.page && (
                  <Pagination
                    totalItems={totalItems}
                    currentPage={parseInt(apiQueryParams.page as string, 10)}
                    itemsPerPage={pageSize}
                    handleClick={handlePageChange}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default RidersList;
